import React, { useEffect, useState } from "react";
import { api, errText } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];
const FITS = ["slim", "regular", "relaxed", "oversized"];

const Profile = () => {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [height, setHeight] = useState("");
  const [size, setSize] = useState("");
  const [fit, setFit] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    setName(user.name || "");
    setHeight(user.height_cm ? String(user.height_cm) : "");
    setSize(user.size || "");
    setFit(user.fit_preference || "");
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/auth/profile", {
        name,
        height_cm: height ? Number(height) : null,
        size: size || null,
        fit_preference: fit || null,
      });
      toast.success("Measurements saved — future try-ons will drape closer to life");
    } catch (err) {
      toast.error(errText(err, "Could not save"));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
      <div className="grid grid-cols-12 gap-10">
        <aside className="col-span-12 md:col-span-5 md:sticky md:top-32 self-start">
          <div className="font-mono-eyebrow">The tailor's notebook</div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-none mt-2">Your<br/><em className="text-[color:var(--accent)]">silhouette.</em></h1>
          <p className="mt-8 text-black/60 leading-relaxed max-w-md">
            A few measurements let the AI cut fabric closer to your reality — the drape,
            the shoulder, the hem. Everything is optional. Everything is private.
          </p>
        </aside>
        <form onSubmit={save} className="col-span-12 md:col-span-7 space-y-10 border border-black/10 bg-white/60 p-8 md:p-12">
          <div>
            <label className="font-mono-eyebrow">Full name</label>
            <input data-testid="profile-name" value={name} onChange={(e)=>setName(e.target.value)} className="field mt-2"/>
          </div>
          <div className="grid grid-cols-2 gap-8">
            <div>
              <label className="font-mono-eyebrow">Height (cm)</label>
              <input data-testid="profile-height" type="number" min="120" max="230" value={height} onChange={(e)=>setHeight(e.target.value)} className="field mt-2" placeholder="e.g. 178"/>
            </div>
            <div>
              <label className="font-mono-eyebrow">Regular size</label>
              <div className="flex gap-2 mt-3 flex-wrap">
                {SIZES.map(s => (
                  <button type="button" key={s}
                    data-testid={`profile-size-${s}`}
                    onClick={()=>setSize(s === size ? "" : s)}
                    className={`px-4 py-2 border font-mono-eyebrow transition-colors ${size===s?"bg-black text-[#F6F5F1] border-black":"border-black/20 hover:border-black"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <label className="font-mono-eyebrow">Fit preference</label>
            <div className="flex gap-2 mt-3 flex-wrap">
              {FITS.map(f => (
                <button type="button" key={f}
                  data-testid={`profile-fit-${f}`}
                  onClick={()=>setFit(f === fit ? "" : f)}
                  className={`px-4 py-2 border font-mono-eyebrow capitalize transition-colors ${fit===f?"bg-black text-[#F6F5F1] border-black":"border-black/20 hover:border-black"}`}>
                  {f}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end pt-4 border-t border-black/10">
            <button data-testid="profile-save" type="submit" disabled={busy} className="btn-ink disabled:opacity-50">
              {busy ? "Saving…" : "Save measurements"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Profile;
